import { NextPage } from 'next';
import { useTranslation, WithTranslation } from 'react-i18next';
import { Store } from 'redux';
import { LogicMiddleware } from 'redux-logic';
import { RootState, wrapper } from 'store/index';

import { loadProBlogPostByPostId } from 'modules/users/service';
import { loadPro, proSelector } from 'modules/users/duck';
import { getDetail, jobTypeSelector } from 'modules/job-types/duck';

import Layout from 'modules/layout';
import ProBlogPostContent from 'modules/users/modules/blog/components/ProBlogPostContent';

import { setupPageAndLoadTranslations } from 'utils/page';

import { JobType } from 'src/modules/job-types/types';
import { ProBlogPostProps } from 'components/molecules/ProBlogPost';
import type { SeoObject } from 'modules/marketing-pages/marketing-page.types';

import styles from 'modules/users/modules/blog/components/ProBlogPostContent.module.scss';

interface Props extends WithTranslation {
  blogPost: ProBlogPostProps;
  jobType: JobType;
  pro: {
    id: string;
    location: {
      city?: { key: string; name: string };
      prefecture?: { key: string; name: string };
    };
    name?: string;
    profile: Record<string, unknown>;
    slug: string;
  };
  seo: SeoObject;
  slug: string;
  title: string;
  userId: string;
}

const ProBlogPostPage: NextPage<Props> = ({ blogPost, jobType, pro, seo, slug, title }) => {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <Layout language={language} seo={seo} title={title}>
      <div className={styles['pro-blog-container']}>
        <ProBlogPostContent blogPost={blogPost} jobType={jobType} pro={pro} slug={slug} />
      </div>
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ defaultLocale, locale, query }) => {
      const { dispatch, getState, logicMiddleware } = store as Store<RootState> & {
        logicMiddleware: LogicMiddleware;
      };

      const { postId, userId } = query as { postId: string; userId: string };

      const { currentLocale, i18nData } = await setupPageAndLoadTranslations({
        defaultLocale,
        locale,
        namespaces: ['common', 'marketingPages', 'request', 'users', 'form'],
        // @ts-expect-error Extended store
        store,
      });

      const blogPost = await loadProBlogPostByPostId(postId);

      if (!blogPost) {
        return { notFound: true };
      }

      const { jobTypeId } = blogPost;

      dispatch(loadPro(userId));
      dispatch(getDetail(jobTypeId));
      await logicMiddleware.whenComplete();

      const pro = proSelector(getState()) as
        | {
            deactivated?: boolean;
            id: string;
            profile: { languages: Record<string, unknown> };
            slug?: string;
          }
        | undefined;

      if (!pro || pro.deactivated) {
        return { notFound: true };
      }

      const jobType = jobTypeSelector(store.getState(), { jobTypeId });

      const encodedSlug = encodeURIComponent(pro.slug ?? '');

      // if pro has slug and userId is pro's id then we redirect to page using slug
      if (encodedSlug && userId === pro.id) {
        return {
          redirect: {
            destination: `${
              currentLocale === 'en' ? '/en' : ''
            }/profile/${encodedSlug}/pro/blog/${postId}`,
            permanent: true,
          },
        };
      }

      const seo: SeoObject = {};

      seo.noIndex = true;

      return {
        props: {
          ...i18nData,
          blogPost,
          jobType,
          pro,
          seo,
          slug: pro.slug,
          title: blogPost.title ?? '',
        },
      };
    }
);

export default ProBlogPostPage;
